import { Component, computed, effect, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject } from 'rxjs';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Brand } from '../../../models/finance/brand';
import { BusinessEntityType } from '../../../models/finance/business';
import { CommandSearchFabric } from '../../../models/fabric/fabric-seach';
import { Customer } from '../../../models/finance/customer';
import { Fabric } from '../../../models/fabric/fabric';
import {
  ProductDevelopmentAdminGroup,
  ProductDevelopmentEditorGroup,
  ProductDevelopmentViewerGroup,
} from '../../../models/authorization/enum/user-group.enum';
import { EnumService } from '../../../services/enum.service';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { ProductDevelopmentStatus } from '@app/models/enums/product-development-status';

@Component({
  selector: 'app-fabric',
  templateUrl: './fabric.component.html',
  styleUrl: './fabric.component.scss',
})
export class FabricComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private fabricProxyService = inject(FabricProxyService);
  private messageService = inject(MessageService);
  private businessProxyService = inject(BusinessProxyService);
  private confirmationService = inject(ConfirmationService);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);

  private destroy$ = new Subject<void>();
  private lastSearchCriteria: CommandSearchFabric = {};

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'fabric-filters',
    config: computed(() => (this.enumsReady() ? this.filterConfig() : null)),
  });
  showFilters = signal<boolean>(true);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  // Data
  listFabrics = signal<Fabric[]>([]);
  brands = signal<Brand[]>([]);
  customers = signal<Customer[]>([]);
  statuses = signal<EnumValues>({});
  enumsReady = signal(false);

  canEdit = computed(() => {
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.FabricAdmin) || AuthService.hasRole(ProductDevelopmentEditorGroup.FabricEditor)) {
      return true;
    }
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.FabricViewer)) {
      return false;
    }
    return false;
  });

  getRowData(rowData: unknown): Fabric {
    return rowData as Fabric;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('ProductDevelopmentStatus', 'itemEnums').values;
        if (Object.keys(statusesValue).length > 0) {
          this.statuses.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit() {
    this.loadCustomersAndBrands();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const statusOptions = Object.entries(this.statuses()).map(([key, value]) => ({
      value: key,
      label: value,
    }));

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'fabricCode',
          label: translate('Fabric_Code', 'Code'),
          placeholder: translate('Enter_fabric_code', 'Enter fabric code'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'longDescription',
          label: translate('Fabric_Description', 'Description'),
          placeholder: translate('Enter_fabric_description', 'Enter description'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'status',
          label: translate('Fabric_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: statusOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  loadCustomersAndBrands(): void {
    this.startLoading();
    this.businessProxyService
      .search({
        businessTypes: [BusinessEntityType.Brand, BusinessEntityType.Client],
      })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (results) => {
          if (!results.data || results.data?.items.length === 0) {
            this.messageService.add({
              severity: 'warn',
              summary: 'No Data',
              detail: this.translateService.instant('Fabric_Not_Brands_Customer_MSG'),
              key: 'br',
              life: 2500,
            });
            return;
          }
          this.brands.set(results.data.items.filter((b) => b.businessTypes?.includes(BusinessEntityType.Brand)) || []);
          this.customers.set(results.data.items.filter((c) => c.businessTypes?.includes(BusinessEntityType.Client)) || []);
        },
      });
  }

  getCustomer(customerReference: string): Customer | undefined {
    return this.customers().find((customer) => customer.id === customerReference);
  }

  getBrand(brandReference: string): Brand | undefined {
    return this.brands().find((brand) => brand.id === brandReference);
  }

  onSearch(searchCriteria: CommandSearchFabric): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.fabricProxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.listFabrics.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: () => {
          this.listFabrics.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order ?? -1);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(values: DynamicFilterValues): void {
    const filters = values as CommandSearchFabric;
    const searchCriteria: CommandSearchFabric = {
      ...filters,
      status: filters.status?.map((status) => Number(status)),
    };
    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  goToDetails(id?: string): void {
    const queryParams = id ? { id: id } : {};
    this.router.navigate(['/fabric-details'], { queryParams });
  }

  deletePopup(event: Event, fabric: Fabric) {
    event.stopPropagation();
    this.confirmationService.confirm({
      key: 'deleteFabric',
      target: event.target as EventTarget,
      message: `${this.translateService.instant('Fabric_Delete_Dialog')} ${fabric.fabricCode}?`,
      header: this.translateService.instant('Common_Header_Dialog_Danger'),
      icon: 'pi pi-info-circle',
      rejectLabel: this.translateService.instant('Common_Cancel'),
      rejectButtonProps: {
        label: this.translateService.instant('Common_Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translateService.instant('Common_Delete'),
        severity: 'danger',
      },

      accept: () => {
        this.deleteFabric(fabric);
      },
    });
  }

  deleteFabric(fabric: Fabric) {
    this.fabricProxyService.updateFabric(fabric.id!, { ...fabric, status: 98 }).subscribe({
      next: (response) => {
        if (response.success) {
          this.listFabrics.update((currentFabrics) => currentFabrics.filter((f) => f.id !== fabric.id));

          this.messageService.add({
            severity: 'success',
            summary: `${this.translateService.instant('Fabric')} ${fabric?.fabricCode} ${this.translateService.instant('Common_Deleted')}`,
            key: 'br',
            life: 2500,
          });
        }
      },
    });
  }

  canDelete(fabric: Fabric): boolean {
    return this.canEdit() && fabric.status === ProductDevelopmentStatus.Draft;
  }
}
